import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { detectLocale } from '../story/caseModel.js';
import { DialoguePlayback } from './dialoguePlayback.js';

/**
 * A single comic-style speech bubble, anchored to whichever character is
 * "talking" — this is how Patch and Bit pose the case's questions to the
 * player, instead of a narrator reciting case text in the console below.
 *
 * One instance is reused for both characters (only one speaks at a time):
 * `type()` repositions it, recolors its outline to the speaker, and reveals
 * the line like a typewriter. The player acknowledges each finished line;
 * revealing it and advancing it are deliberately separate interactions.
 */
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const PADDING_X = 12;
const PADDING_Y = 9;
const TAIL_HALF_WIDTH = 7;
const TAIL_HEIGHT = 11;
const EDGE_MARGIN = 8;

export class SpeechBubble {
  readonly view = new Container();
  private readonly shape = new Graphics();
  private readonly label: Text;
  private readonly speakerLabel: Text;
  private readonly footer: Text;
  private readonly playback = new DialoguePlayback();
  private readonly locale = detectLocale();
  private readonly screenWidth: number;
  private readonly maxWidth: number;

  private anchorX = 0;
  private anchorY = 0;
  private color = 0xffffff;
  private resolveIdle: (() => void) | undefined;
  private bubbleWidth = 40;
  private bubbleHeight = 40;

  constructor(screenWidth: number, maxWidth: number) {
    this.screenWidth = screenWidth;
    this.maxWidth = maxWidth;
    this.view.addChild(this.shape);
    this.speakerLabel = new Text({
      text: '',
      style: new TextStyle({
        fill: 0xffffff,
        fontFamily: MONO,
        fontSize: 9,
        fontWeight: '700',
        letterSpacing: 1.2,
      }),
    });
    this.view.addChild(this.speakerLabel);
    this.label = new Text({
      text: '',
      style: new TextStyle({
        fill: 0xeaf6ee,
        fontFamily: MONO,
        fontSize: 11.5,
        lineHeight: 15,
        wordWrap: true,
        wordWrapWidth: maxWidth - PADDING_X * 2,
      }),
    });
    this.view.addChild(this.label);
    this.footer = new Text({ text: '', style: { fontFamily: MONO, fontSize: 9, fill: 0xabc7b9 } });
    this.view.addChild(this.footer);
    this.label.eventMode = 'none';
    this.speakerLabel.eventMode = 'none';
    this.footer.eventMode = 'none';
    this.view.visible = false;

    // One tap reveals; another acknowledges. There is no automatic timeout.
    this.shape.eventMode = 'static';
    this.shape.cursor = 'pointer';
    this.shape.on('pointertap', () => {
      this.skip();
    });
  }

  get busy(): boolean {
    return this.view.visible && this.playback.active;
  }

  get typing(): boolean {
    return this.playback.typing;
  }

  /**
   * Shows the bubble with its tail pointing at (anchorX, anchorY) — the
   * character's head. Resolves when the player acknowledges the complete
   * line, or when hide() cancels it during scene disposal.
   */
  async type(
    text: string,
    anchorX: number,
    anchorY: number,
    color: number,
    speaker: string,
  ): Promise<void> {
    this.resolveIdle?.(); // a still-pending previous type() never got its resolve; release it
    this.playback.start(text);
    this.anchorX = anchorX;
    this.anchorY = anchorY;
    this.color = color;
    this.speakerLabel.text = speaker;
    // Measure the final line once. The bubble must not resize and jump around
    // while letters appear.
    this.label.text = text;
    this.bubbleWidth = this.maxWidth;
    this.bubbleHeight = Math.max(this.label.height, 15) + PADDING_Y * 2 + 34;
    this.label.text = '';
    this.view.visible = true;
    this.view.alpha = 1;
    this.updateFooter();
    this.redraw();
    return new Promise((resolve) => {
      this.resolveIdle = resolve;
    });
  }

  /** Reveal first; only a subsequent deliberate tap advances the conversation. */
  skip(): void {
    const completed = this.playback.advance();
    this.label.text = this.playback.text;
    this.updateFooter();
    if (completed) {
      this.resolveIdle?.();
      this.resolveIdle = undefined;
    }
  }

  hide(): void {
    this.view.visible = false;
    this.playback.cancel();
    this.resolveIdle?.();
    this.resolveIdle = undefined;
  }

  private redraw(): void {
    const bubbleWidth = this.bubbleWidth;
    const bubbleHeight = this.bubbleHeight;

    let left = this.anchorX - bubbleWidth / 2;
    left = Math.max(EDGE_MARGIN, Math.min(this.screenWidth - EDGE_MARGIN - bubbleWidth, left));
    const top = Math.max(54, this.anchorY - bubbleHeight - TAIL_HEIGHT);
    const tailX = Math.max(
      left + TAIL_HALF_WIDTH * 2,
      Math.min(left + bubbleWidth - TAIL_HALF_WIDTH * 2, this.anchorX),
    );
    const bottom = top + bubbleHeight;

    this.shape
      .clear()
      .roundRect(left, top, bubbleWidth, bubbleHeight, 9)
      .fill({ color: 0x06130f, alpha: 0.95 })
      .stroke({ color: this.color, width: 1.6, alpha: 0.9 })
      .poly([
        tailX - TAIL_HALF_WIDTH,
        bottom,
        tailX + TAIL_HALF_WIDTH,
        bottom,
        this.anchorX,
        this.anchorY,
      ])
      .fill({ color: 0x06130f, alpha: 0.95 })
      .moveTo(tailX - TAIL_HALF_WIDTH, bottom)
      .lineTo(this.anchorX, this.anchorY)
      .lineTo(tailX + TAIL_HALF_WIDTH, bottom)
      .stroke({ color: this.color, width: 1.6, alpha: 0.9 });

    this.speakerLabel.style.fill = this.color;
    this.speakerLabel.position.set(left + PADDING_X, top + 6);
    this.label.position.set(left + PADDING_X, top + PADDING_Y + 12);
    this.footer.position.set(left + PADDING_X, top + bubbleHeight - 17);
  }

  private updateFooter(): void {
    const copy =
      this.locale === 'fr-FR'
        ? ['Toucher : tout afficher', 'Toucher : continuer →']
        : this.locale === 'zh-CN'
          ? ['点击显示全文', '点击继续 →']
          : ['Tap to reveal', 'Tap to continue →'];
    this.footer.text = copy[this.typing ? 0 : 1] ?? '';
  }

  update(deltaMs: number): void {
    if (!this.busy) return;
    this.playback.update(deltaMs);
    this.label.text = this.playback.text;
    this.updateFooter();
  }

  destroy(): void {
    this.hide();
    this.view.destroy({ children: true });
  }
}
